// Student Desk — the single unified MyDeskey home page.
//
// This page merges what used to live on /student/institution into one
// cohesive desk so multi-institution students see EVERYTHING for their
// currently-active school in a single scroll:
//
//   1. Desk header in the theme colour (+ active institution stats: classes, lessons,
//      per-institution credits, super credits)
//   4. Pending invitations (Accept / Decline)
//   6. My Classes (active institution)
//   7. Recently Published lessons (active institution)
//
// Every data block re-fetches whenever activeTid flips, so a student in
// multiple schools sees a completely different desk per institution —
// different classes, lessons, and credits.
import React, { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import api from './studentApi';
import { useStudent } from './StudentApp';
import { toast } from 'sonner';
import { ArrowRight, GraduationCap, Mail, Check, X, BookOpen } from 'lucide-react';
import { ST_CARD, ST_CARD_LINK, ST_BTN, stPrimaryStyle, stSecondaryStyle, TINT_PRIMARY, TINT_ACCENT, SectionTitle, EmptyNote } from './studentUi';
import { notifyError } from '../../lib/errors';
import { DeskHeroStatBones, DeskSectionsSkeleton } from './StudentSkeletons';

export default function StudentDeskPage() {
  const nav = useNavigate();
  const { me, memberships, activeTid, refreshMe } = useStudent();
  const [busyId, setBusyId] = useState(null);

  // ── Cached per active institution; refetched after accept/decline ────────
  const { data: desk, isPending, refetch } = useQuery({
    queryKey: ['student', 'desk', activeTid],
    queryFn: async () => {
      const [overview, hub, recList, jrLegacy] = await Promise.all([
        activeTid ? api.get('/student/active-institution/overview') : Promise.resolve(null),
        api.get('/student/active-institutions/list'),
        api.get('/student/recommendations').catch(() => ({ recommendations: [] })),
        api.get('/auth/my-join-requests').catch(() => ({ join_requests: [] })),
      ]);
      return { overview, hub, recos: recList.recommendations || [], requests: jrLegacy.join_requests || [] };
    },
    meta: { errorMessage: "Couldn't load your school details." },
  });
  const loadAll = useCallback(() => refetch(), [refetch]);
  const overview = desk?.overview ?? null;   // /student/active-institution/overview
  const hub = desk?.hub ?? null;             // /student/active-institutions/list
  const requests = desk?.requests ?? [];     // legacy /auth/my-join-requests (back-compat)
  const loaded = !isPending;                 // first fetch done (stays true on refetch)

  // ── Accept / decline a pending invite (both shapes) ──────────────────────
  const acceptInvite = useCallback(async (jrId, instName) => {
    setBusyId(jrId);
    try {
      await api.post('/student/invites/accept', { join_request_id: jrId });
      toast.success(`Joined ${instName || 'the institution'}`);
      await refreshMe();
      await loadAll();
    } catch (e) {
      notifyError(e, 'Could not accept invite');
    } finally { setBusyId(null); }
  }, [refreshMe, loadAll]);

  const declineInvite = useCallback(async (jrId) => {
    setBusyId(jrId);
    try {
      await api.post('/student/invites/decline', { join_request_id: jrId });
      toast.success('Invite declined');
      await loadAll();
    } catch (e) {
      notifyError(e, 'Could not decline invite');
    } finally { setBusyId(null); }
  }, [loadAll]);

  // Legacy join-requests endpoint (kept for back-compat with old admin invites)
  const respondLegacy = async (id, action) => {
    setBusyId(id);
    try {
      await api.post(`/auth/join-request/${id}/respond`, { action });
      toast.success(action === 'accept' ? 'Joined' : 'Request declined');
      await refreshMe();
      await loadAll();
    } catch (e) {
      notifyError(e, 'Failed');
    } finally { setBusyId(null); }
  };

  // ── Derived state ────────────────────────────────────────────────────────
  const profile = me?.student_profile || {};
  const classes = overview?.classes || [];
  const recentTopics = overview?.recent_topics || [];
  const tenant = overview?.tenant;
  const credits = overview?.credits;
  const superCredits = hub?.super_credits ?? me?.super_credits ?? 0;
  const pendingJoinRequests = hub?.pending_join_requests || [];
  const pendingInvitesPre = hub?.pending_invites || [];
  const pendingTotal = pendingJoinRequests.length + pendingInvitesPre.length + requests.length;

  return (
    <div className="space-y-10" data-testid="student-desk">

      {/* ── 1) Desk header (+ active institution figures) ──────────────── */}
      <DeskHero studentName={me?.full_name} schoolName={tenant?.name} interests={profile.interests}
        stats={activeTid && tenant ? {
          tenant, credits, superCredits,
          classCount: classes.length, lessonCount: recentTopics.length,
        } : null}
        statsLoading={!loaded && !!activeTid} />
      {!loaded && activeTid && <DeskSectionsSkeleton />}
      {memberships.length === 0 && <EmptyJoinCard />}

      {/* ── 4) Pending Invitations ─────────────────────────────────────── */}
      {pendingTotal > 0 && (
        <section data-testid="pending-invites-section">
          <SectionTitle title="Invitations" count={pendingTotal} />
          <div className="space-y-2">
            {pendingJoinRequests.map(jr => (
              <InviteRow key={jr.id} kind="join_request" row={jr}
                acting={busyId === jr.id}
                onAccept={() => acceptInvite(jr.id, jr.institution?.name || jr.institution?.tenant_name)}
                onDecline={() => declineInvite(jr.id)} />
            ))}
            {pendingInvitesPre.map(inv => (
              <InviteRow key={inv.id} kind="pre_signup_invite" row={inv}
                acting={false} hint="We emailed you a sign-up link. Finish there to claim your place" />
            ))}
            {/* Legacy /auth/my-join-requests for back-compat */}
            {requests.map(r => (
              <div key={r.id} className={`${ST_CARD} p-4 flex flex-wrap items-center gap-3`} data-testid={`join-req-${r.id}`}>
                <Mail className="w-4 h-4" style={{ color: 'var(--st-mute)' }} />
                <div className="flex-1 min-w-[200px]">
                  <div className="text-sm font-medium">{r.institution_name}</div>
                  <div className="text-sm" style={{ color: 'var(--st-mute)' }}>Invited as {r.requested_role} · {r.licence_duration_months || 12} months</div>
                </div>
                <button onClick={() => respondLegacy(r.id, 'decline')} disabled={busyId === r.id} className={ST_BTN} style={stSecondaryStyle} data-testid={`decline-${r.id}`}>
                  <X className="w-4 h-4" /> Decline
                </button>
                <button onClick={() => respondLegacy(r.id, 'accept')} disabled={busyId === r.id} className={ST_BTN} style={stPrimaryStyle} data-testid={`accept-${r.id}`}>
                  <Check className="w-4 h-4" /> Accept and join
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── 6) My Classes (active institution) ─────────────────────────── */}
      {activeTid && tenant && (
        <section data-testid="classes-section">
          <SectionTitle title="My classes" count={classes.length} />
          {classes.length === 0 ? (
            <EmptyNote>You aren't in any classes at this school yet</EmptyNote>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {classes.map(c => (
                <button key={c.id} onClick={() => nav(`/student/class/${c.id}`)} data-testid={`class-card-${c.id}`}
                  className={`${ST_CARD_LINK} text-left p-5 flex flex-col`}>
                  <div className="flex items-start gap-3">
                    <span className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={TINT_PRIMARY}>
                      <BookOpen className="w-5 h-5" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-base font-semibold truncate">{c.name}</div>
                      <div className={`text-sm truncate ${c.subjects?.length ? 'capitalize' : ''}`} style={{ color: 'var(--st-mute)' }}>{(c.subjects || []).join(' · ') || 'No subjects yet'}</div>
                    </div>
                  </div>
                  {c.latest_topic && (
                    <div className="mt-4 pt-4 border-t text-sm truncate" style={{ borderColor: 'var(--st-border)' }}>
                      <span className="inline-block w-1.5 h-1.5 rounded-full mr-2 align-middle" style={{ background: 'var(--st-accent)' }} aria-hidden="true" />
                      <span style={{ color: 'var(--st-mute)' }}>Latest </span><span className="font-medium">{c.latest_topic.title}</span>
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </section>
      )}

      {/* ── 7) Recently Published ──────────────────────────────────────── */}
      {activeTid && tenant && recentTopics.length > 0 && (
        <section data-testid="recent-topics-section">
          <SectionTitle title="New from your teachers" count={recentTopics.length} />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {recentTopics.map(t => (
              <button key={t.id} onClick={() => nav(`/student/topic/${t.id}`)} data-testid={`topic-row-${t.id}`}
                className={`${ST_CARD_LINK} text-left p-4 flex items-center gap-4 group`}>
                <DateTile date={t.topic_date} />
                <div className="flex-1 min-w-0">
                  <div className="text-base font-semibold truncate">{t.title}</div>
                  <div className="text-sm truncate" style={{ color: 'var(--st-mute)' }}>{t.summary}</div>
                </div>
                <ArrowRight className="w-4 h-4 flex-shrink-0 transition-[transform,color] group-hover:translate-x-0.5 text-[var(--st-mute)] group-hover:text-[var(--st-primary-ink)]" />
              </button>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Sub-components
// ════════════════════════════════════════════════════════════════════════

// Calendar-style date block for a published lesson.
function DateTile({ date }) {
  const d = date ? new Date(date) : null;
  return (
    <span className="w-12 h-12 rounded-lg flex flex-col items-center justify-center flex-shrink-0 leading-none" style={TINT_ACCENT}>
      <span className="text-xs font-medium">{d ? d.toLocaleDateString('en', { month: 'short' }) : '—'}</span>
      <span className="text-xl font-semibold tabular-nums mt-0.5" style={{ fontFamily: 'var(--st-font-heading)' }}>{d ? d.getDate() : ''}</span>
    </span>
  );
}

// The desk's one bright element: a card in the student's own theme colour
// (fading to a deeper shade of the same hue, with a faint dotted-notebook
// texture on the right), holding the page title and the active school's figures.
const HERO_STYLE = {
  ...stPrimaryStyle,
  background: 'linear-gradient(120deg, var(--st-primary) 0%, var(--st-primary) 40%, var(--st-primary-deep) 100%)',
};
const HERO_DOTS = {
  backgroundImage: 'radial-gradient(color-mix(in srgb, var(--st-on-primary) 22%, transparent) 1.2px, transparent 1.2px)',
  backgroundSize: '18px 18px',
  maskImage: 'linear-gradient(90deg, transparent 35%, #000 100%)',
  WebkitMaskImage: 'linear-gradient(90deg, transparent 35%, #000 100%)',
};
function DeskHero({ studentName, schoolName, interests, stats, statsLoading }) {
  const firstName = (studentName || '').trim().split(/\s+/)[0];
  const today = new Date().toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' });
  const meta = [today, schoolName].filter(Boolean).join(' · ');
  return (
    <section data-testid="student-desk-greeting">
      <div className="relative overflow-hidden rounded-[var(--st-radius-card)] p-6 sm:p-8 shadow-[0_8px_24px_-12px_color-mix(in_srgb,var(--st-primary)_60%,transparent)]" style={HERO_STYLE}>
        <div className="absolute inset-0 pointer-events-none" style={HERO_DOTS} aria-hidden="true" />
        <div className="relative flex flex-col lg:flex-row lg:items-end gap-6">
          <div className="flex-1 min-w-0">
            <p className="text-sm opacity-80">{meta}</p>
            <h1 className="text-4xl sm:text-5xl font-semibold mt-2 leading-none" style={{ color: 'inherit' }}>{firstName ? `Hi, ${firstName}` : 'Student Desk'}</h1>
            {interests?.length > 0 && (
              <p className="text-sm mt-3 opacity-80 max-w-xl">Picked for {interests.slice(0, 4).join(', ')}</p>
            )}
          </div>
          {statsLoading && !stats && <DeskHeroStatBones />}
          {stats && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-y-4" data-testid="active-institution-stats">
              <Stat label="Classes" value={stats.classCount} />
              <Stat label="Lessons" value={stats.lessonCount} />
              <Stat label={`${(stats.tenant.name || 'School').split(' ')[0]} credits`} value={stats.credits?.credits_remaining ?? 0} />
              <Stat label="Super credits" value={stats.superCredits} />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function InviteRow({ kind, row, acting, hint, onAccept, onDecline }) {
  const inst = row.institution || {};
  return (
    <div className={`${ST_CARD} p-4 flex items-start gap-3`} data-testid={`invite-row-${row.id}`}>
      <span className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={TINT_PRIMARY}>
        <Mail className="w-4 h-4" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{inst.name || 'Institution'}</div>
        <div className="text-sm mt-0.5" style={{ color: 'var(--st-mute)' }}>
          As <span className="capitalize">{row.requested_role || row.role || 'student'}</span>
          {row.licence_duration_months && <> · {row.licence_duration_months} month licence</>}
          {row.invited_by_name && <> · from {row.invited_by_name}</>}
        </div>
        {hint && <div className="text-sm mt-1" style={{ color: 'var(--st-mute)' }}>{hint}</div>}
      </div>
      {kind === 'join_request' && (
        <div className="flex items-center gap-2">
          <button onClick={onDecline} disabled={acting} data-testid={`invite-decline-${row.id}`} className={ST_BTN} style={stSecondaryStyle}>
            Decline
          </button>
          <button onClick={onAccept} disabled={acting} data-testid={`invite-accept-${row.id}`} className={ST_BTN} style={stPrimaryStyle}>
            {acting ? 'Joining…' : 'Accept'}
          </button>
        </div>
      )}
    </div>
  );
}

function EmptyJoinCard() {
  return (
    <div className={`${ST_CARD} p-8 sm:p-10 text-center max-w-2xl`} data-testid="no-active-tenant">
      <span className="w-12 h-12 mx-auto rounded-lg flex items-center justify-center" style={TINT_PRIMARY}>
        <GraduationCap className="w-6 h-6" />
      </span>
      <h3 className="text-xl font-semibold mt-4">You haven't joined a school yet</h3>
      <p className="text-sm mt-2 max-w-md mx-auto" style={{ color: 'var(--st-mute)' }}>
        Give your school or college admin the email you signed up with. They'll send an invitation here,
        and you can join as many schools as you need from one account.
      </p>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="min-w-0 px-5 first:pl-0 sm:border-l sm:first:border-l-0" style={{ borderColor: 'color-mix(in srgb, var(--st-on-primary) 22%, transparent)' }}>
      <div className="text-sm opacity-80 truncate">{label}</div>
      <div className="text-3xl font-semibold tabular-nums leading-tight mt-1" style={{ fontFamily: 'var(--st-font-heading)' }}>{value}</div>
    </div>
  );
}
